<script>
import DataLayer from '@services/data-layer/index'

// `currentChart` is declared here rather than in setup because vue-meta evaluates page() through
// a watcher that cannot see <script setup> bindings, and the chart is fetched async so page()
// cannot re-derive it from $route or $store. Setup writes it once the fetch lands.
export default {
    data() {
        return {
            currentChart: null,
        }
    },

    page() {
        const title = this.currentChart ? `${this.currentChart.revisions_title} - Chart` : 'Chart'
        const description = (this.currentChart && this.currentChart.revisions_description) || 'A chart on FRISKY'
        const image = (this.currentChart && this.currentChart.revisions_album_art && this.currentChart.revisions_album_art.url)
            || `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'music.playlist' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import { useQueueService } from '@composables/use-queue-service'
import Layout from '@layouts/main-layout'
import PlaylistsTableBlock from '@components/playlists/playlists-table-block'
import PlaylistInfo from '@components/playlists/playlist-info'
import modalService from '@/services/modal-service'

const PAGE_SIZE = 20
const PLACEHOLDER_ROWS = 20
const LOAD_MORE_THROTTLE_MS = 200
const BOTTOM_OFFSET_RATIO = 3

const instance = getCurrentInstance()
const route = useRoute()
const store = useStore()
const queueService = useQueueService()

// Setup keeps its own copy under a different name: a binding called `currentChart` would shadow
// the options data property the template and page() rely on.
const chart = ref(null)

const chartMixes = ref(
    Array.from(Array(PLACEHOLDER_ROWS), (_mix, i) => ({
        id: Number.MAX_SAFE_INTEGER - i,
        genre: [],
    }))
)
const shouldTransition = ref(false)
const maxMixes = ref(PAGE_SIZE)
const totalMixes = ref(null)
const shouldLoadMore = ref(false)
const loaded = ref(false)

const id = computed(() => route.params.id)

const scroll = computed(() => store.state.appearance.scroll)

const loggedIn = computed(() => store.getters['auth/loggedIn'])

const currentPosition = computed(() => store.state.queue.position)

const hasReachedMaxMixes = computed(() => maxMixes.value >= Number(totalMixes.value))

function loadMoreMixes(fillAll = false) {
    loaded.value = false
    maxMixes.value += PAGE_SIZE
    const wanted = fillAll ? totalMixes.value - chartMixes.value.length : PAGE_SIZE

    return DataLayer.playlists
        .getRevisionsMixes(id.value, wanted, chartMixes.value.length)
        .then(mixes => {
            loaded.value = true
            return chartMixes.value.push(...mixes.body.map(revision => revision.mixes))
        })
}

function playPlaylist(position) {
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }

    const play = () =>
        queueService.playMixMultiple({
            mixes: [...chartMixes.value],
            id: chart.value.id,
            position,
        })

    // Playing the chart queues all of it, so anything not paged in yet has to be fetched first.
    if (chartMixes.value.length === totalMixes.value) {
        play()
    } else {
        loadMoreMixes(true).then(play)
    }
}

watch(scroll, () => {
    if (hasReachedMaxMixes.value) return

    shouldLoadMore.value = true

    const bottomOfWindow =
        scroll.value + window.innerHeight >=
        store.state.appearance.scrollChild.offsetHeight - window.innerHeight / BOTTOM_OFFSET_RATIO

    if (bottomOfWindow && shouldLoadMore.value && loaded.value) {
        shouldLoadMore.value = false
        setTimeout(() => {
            shouldLoadMore.value = true
        }, LOAD_MORE_THROTTLE_MS)
        loadMoreMixes()
    }
})

onMounted(() => {
    DataLayer.playlists.getById(id.value).then(res => {
        chart.value = res.body
        // page() reads this off the instance; `$data` is initialised by the time onMounted runs.
        instance.proxy.currentChart = res.body
    })

    DataLayer.playlists.getRevisionsMixes(id.value, PAGE_SIZE, 0).then(res => {
        loaded.value = true
        totalMixes.value = Number(res.headers['x-items-total'])
        chartMixes.value = res.body.map(revision => revision.mixes)
    })
})
</script>

<template>
    <Layout>
        <BaseContent>
            <div class="container">
                <BaseSection padded>
                    <h6 class="title">Chart</h6>
                    <div class="content">
                        <div class="details">
                            <PlaylistInfo
                                :playlist-mixes="chartMixes"
                                :playlist="currentChart"
                                @playPlaylist="playPlaylist(currentPosition !== -1 ? currentPosition : 0)"
                            />
                        </div>
                        <PlaylistsTableBlock
                            :mixes="chartMixes"
                            :error="{
                                title: `There are no mixes in this chart yet`,
                            }"
                            :should-transition="shouldTransition"
                            playlist-id
                            genres
                            is-indexed-list
                            @playPlaylistMix="playPlaylist"
                        />
                    </div>
                </BaseSection>
            </div>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    margin-top: $s-h;
}

.content {
    @include widescreen {
        display: grid;
        grid-template-columns: 1fr 3fr;
        gap: $s-xxxl;
    }
}

.details {
    max-width: 300px;
    margin: 0 auto;
}

.title {
    margin-bottom: $s-xl;
}
</style>
